"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { hashSeed, normalizeAvatar } from "@/lib/avatar/catalog";
import usePrefersReducedMotion from "@/hooks/usePrefersReducedMotion";
import { wearsCustomPiece } from "@/lib/avatar/pieces";
import AvatarFigure from "./AvatarFigure";
import usePieces from "./usePieces";
import styles from "./AvatarCharacter.module.css";

// Durée de chaque réaction, calée sur son animation CSS.
const REACTIONS = { laugh: 1500, wow: 1300 };
const RELEASE_MS = 260;

// `interactive` donne au personnage ses réactions :
// - survol : il suit le pointeur des yeux, penche la tête vers lui, hausse les sourcils et sourit plus franchement ;
// - appui : il ferme les yeux en souriant et recule un peu, déjà dans le rire qui suit ;
// - clic (ou Entrée, Espace) : il rit ;
// - double clic : il s'étonne, yeux ronds et bouche en « o », avec un petit sursaut.
// `reactionLabel` nomme alors le bouton pour les lecteurs d'écran.
export default function AvatarCharacter({
    avatar,
    seed,
    size,
    animated = "auto",
    interactive = false,
    reactionLabel,
    // Humeur tenue tant qu'elle est donnée : "happy" (il rit, tout content) ou "sad"
    // (regard baissé, sourcils tombants, une larme). Un clic garde la priorité.
    mood,
    // Les pièces ajoutées sans redéploiement. Par défaut, elles sont chargées d'elles-mêmes
    // dès que l'avatar en porte une ; l'admin passe les siennes pour prévisualiser un bloc.
    pieces: givenPieces,
    // Faux : le personnage seul, sans son rond ni son fond (bannière de profil).
    framed = true,
    // Une zone plus large que le personnage (ref d'un élément parent) : dès que le pointeur y
    // entre, il le suit des yeux, sans attendre d'être survolé lui-même (bannière de profil).
    trackWithin = null,
    className = "",
    title,
    // Sans `size`, c'est le CSS de l'appelant (classes, style) qui donne la taille.
    style,
    ...rest
}) {
    const id = useId();
    const svgRef = useRef(null);
    const frame = useRef(0);
    const config = useMemo(() => normalizeAvatar(avatar, seed), [avatar, seed]);
    const loadedPieces = usePieces(!givenPieces && wearsCustomPiece(config));
    const pieces = givenPieces || loadedPieces;
    // Les pièces arrivées après le premier affichage apparaissent en fondu ; celles qui
    // étaient déjà là sont dessinées d'emblée, sans animation.
    const lateRef = useRef(!pieces);
    const figureClasses = useMemo(() => ({ ...styles, piece: lateRef.current ? styles.pieceIn : undefined }), []);
    const reducedMotion = usePrefersReducedMotion();

    const [reaction, setReaction] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const [releasing, setReleasing] = useState(false);

    useEffect(() => {
        if (!reaction) return undefined;
        const timer = setTimeout(() => setReaction(null), REACTIONS[reaction]);
        return () => clearTimeout(timer);
    }, [reaction]);

    useEffect(() => {
        if (!releasing) return undefined;
        const timer = setTimeout(() => setReleasing(false), RELEASE_MS);
        return () => clearTimeout(timer);
    }, [releasing]);

    useEffect(() => () => cancelAnimationFrame(frame.current), []);

    // Le regard passe par des variables CSS posées directement sur le SVG : suivre le
    // pointeur ne redessine pas le composant à chaque mouvement.
    const look = (x, y) => {
        const svg = svgRef.current;
        if (!svg) return;
        svg.style.setProperty("--avatar-look-x", `${(x * 1.7).toFixed(2)}px`);
        svg.style.setProperty("--avatar-look-y", `${(y * 1.3).toFixed(2)}px`);
        svg.style.setProperty("--avatar-lean", `${(x * 2.2).toFixed(2)}deg`);
    };
    const onPointerMove = (event) => {
        const { clientX, clientY } = event;
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => {
            const box = svgRef.current?.getBoundingClientRect();
            if (!box) return;
            const clamp = (value) => Math.max(-1, Math.min(1, value));
            // Dans une zone élargie, le regard se règle sur elle : il tourne peu à peu jusqu'à ses bords,
            // au lieu de buter dès que le pointeur sort du personnage.
            const area = trackWithin?.current?.getBoundingClientRect();
            const reachX = area ? Math.max(clientX < box.left + box.width / 2 ? box.left + box.width / 2 - area.left : area.right - box.left - box.width / 2, 1) : box.width / 2;
            const reachY = area ? Math.max(area.height / 2, 1) : box.height / 2;
            // Les yeux sont au-dessus du centre du cadre : le repère du regard aussi.
            look(clamp((clientX - box.left - box.width / 2) / reachX), clamp((clientY - box.top - box.height * 0.47) / reachY));
        });
    };
    const onPointerLeave = () => {
        cancelAnimationFrame(frame.current);
        look(0, 0);
        setHovered(false);
        setPressed(false);
        setReleasing(true);
    };
    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setReaction("laugh");
        }
    };
    // Avec une zone élargie, c'est elle qui écoute le pointeur ; le clic reste sur le personnage.
    const tracked = interactive && Boolean(trackWithin);
    useEffect(() => {
        const area = tracked ? trackWithin.current : null;
        if (!area) return undefined;
        const enter = () => setHovered(true);
        area.addEventListener("pointerenter", enter);
        area.addEventListener("pointermove", onPointerMove);
        area.addEventListener("pointerleave", onPointerLeave);
        return () => {
            area.removeEventListener("pointerenter", enter);
            area.removeEventListener("pointermove", onPointerMove);
            area.removeEventListener("pointerleave", onPointerLeave);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracked]);
    const handlers = interactive
        ? {
              onClick: () => setReaction((current) => (current === "wow" ? current : "laugh")),
              onDoubleClick: () => setReaction("wow"),
              ...(tracked ? {} : { onPointerEnter: () => setHovered(true), onPointerMove, onPointerLeave }),
              onPointerDown: () => setPressed(true),
              onPointerUp: () => setPressed(false),
              onKeyDown,
          }
        : {};

    // En dessous de 32 px, le mouvement ne se lit plus : l'avatar reste fixe.
    const moves = animated === "auto" ? size === undefined || size >= 32 : Boolean(animated);
    // Chaque avatar a son propre décalage, pour qu'une liste ne cligne pas d'un seul bloc.
    const offset = (hashSeed(seed ?? JSON.stringify(config)) % 5000) / -1000;

    // Le visage du moment : la réaction en cours l'emporte sur ce que le membre a composé.
    // Dès l'appui, les yeux prennent la forme rieuse du clic : pas d'entre-deux écrasé.
    const happy = mood === "happy" && !reaction;
    const sad = mood === "sad" && !reaction && !pressed;
    const eyes = { laugh: "happy", wow: "wide" }[reaction] || (pressed || happy ? "happy" : config.eyes);
    const mouth = { laugh: "laugh", wow: "ooh" }[reaction] || (happy ? "laugh" : sad ? "sad" : config.mouth);
    // La bouche change de forme (elle ne grossit pas) : son animation est en SMIL, que le
    // CSS ne sait pas couper, d'où ce test à part pour qui a demandé moins d'animations.
    let mouthMotion = null;
    if (!reducedMotion) {
        if (reaction === "laugh" || happy) mouthMotion = { kind: "laugh" };
        else if (reaction === "wow") mouthMotion = { kind: "hold" };
        else if (sad) mouthMotion = null;
        else if (hovered) mouthMotion = { kind: "hold" };
        else if (releasing) mouthMotion = { kind: "release" };
        else if (moves) mouthMotion = { kind: "idle", begin: `${offset}s` };
    }

    const classes = [
        styles.avatar,
        moves && styles.moves,
        interactive && styles.interactive,
        hovered && styles.hovering,
        pressed && styles.pressed,
        reaction && styles[reaction],
        happy && styles.happy,
        sad && styles.sad,
        className,
    ];

    return (
        <svg
            ref={svgRef}
            viewBox="0 0 120 120"
            width={size}
            height={size}
            role={interactive ? "button" : "img"}
            tabIndex={interactive ? 0 : undefined}
            aria-label={(interactive ? reactionLabel : title) || title || undefined}
            aria-hidden={title || interactive ? undefined : true}
            className={classes.filter(Boolean).join(" ")}
            style={{ "--avatar-offset": `${offset}s`, ...style }}
            {...rest}
            {...handlers}
        >
            <AvatarFigure
                config={config}
                id={id}
                pieces={pieces}
                classes={figureClasses}
                eyes={eyes}
                mouth={mouth}
                mouthMotion={mouthMotion}
                brows={sad ? "sad" : config.brows}
                blush={reaction === "laugh" || happy}
                tear={sad}
                frame={framed}
            />
        </svg>
    );
}
